import { User } from '../types/user';
import { LeaseRequest } from '../types/lease';
import { Listing } from '../types/listing';

// Email templates for different notification types
const emailTemplates = {
  verifyEmail: (verificationLink: string) => ({
    subject: 'Verify Your SubleaseHero Account',
    html: `
      <h1>Welcome to SubleaseHero!</h1>
      <p>Please verify your email address by clicking the link below:</p>
      <a href="${verificationLink}" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        Verify Email Address
      </a>
      <p>This link will expire in 24 hours.</p>
    `
  }),

  welcomeEmail: (user: User) => ({
    subject: 'Welcome to SubleaseHero',
    html: `
      <h1>Welcome to SubleaseHero, ${user.fullName || 'there'}!</h1>
      <p>Thank you for joining our community. Here's what you can do next:</p>
      <ul>
        <li>Complete your profile</li>
        <li>Verify your identity</li>
        <li>Browse available listings</li>
      </ul>
      <a href="https://subleasehero.com/profile" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        Complete Your Profile
      </a>
    `
  }),

  newMessage: (recipientName: string, senderName: string, listingTitle: string) => ({
    subject: 'New Message Received',
    html: `
      <h1>New Message from ${senderName}</h1>
      <p>You have received a new message regarding "${listingTitle}".</p>
      <a href="https://subleasehero.com/messages" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        View Message
      </a>
    `
  }),

  applicationSubmitted: (listing: Listing, request: LeaseRequest) => ({
    subject: 'New Application Received',
    html: `
      <h1>New Application for ${listing.title}</h1>
      <p>You have received a new application from ${request.applicant.name}.</p>
      <ul>
        <li>Move-in Date: ${new Date(request.moveInDate).toLocaleDateString()}</li>
        <li>Monthly Income: $${request.applicant.monthlyIncome}</li>
        <li>Occupation: ${request.applicant.occupation}</li>
      </ul>
      <a href="https://subleasehero.com/lease-requests" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        Review Application
      </a>
    `
  }),

  applicationApproved: (listing: Listing) => ({
    subject: 'Your Application Has Been Approved!',
    html: `
      <h1>Application Approved!</h1>
      <p>Congratulations! Your application for "${listing.title}" has been approved.</p>
      <p>Next steps:</p>
      <ol>
        <li>Pay the good faith deposit</li>
        <li>Review lease terms</li>
        <li>Coordinate move-in details</li>
      </ol>
      <a href="https://subleasehero.com/dashboard" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        View Details
      </a>
    `
  }),

  applicationRejected: (listing: Listing) => ({
    subject: 'Application Update',
    html: `
      <h1>Application Status Update</h1>
      <p>We regret to inform you that your application for "${listing.title}" was not approved at this time.</p>
      <p>Continue browsing other available properties:</p>
      <a href="https://subleasehero.com/listings" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        Browse Listings
      </a>
    `
  }),

  depositReceived: (listing: Listing, amount: number) => ({
    subject: 'Deposit Received - Next Steps',
    html: `
      <h1>Deposit Received</h1>
      <p>We've received your deposit of $${amount} for "${listing.title}".</p>
      <p>Next steps:</p>
      <ol>
        <li>Contact information has been shared with both parties</li>
        <li>Coordinate with the property owner for:</li>
        <ul>
          <li>Security deposit payment</li>
          <li>Lease agreement signing</li>
          <li>Move-in coordination</li>
        </ul>
      </ol>
      <a href="https://subleasehero.com/dashboard" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        View Details
      </a>
    `
  }),

  verificationApproved: (type: 'id' | 'address') => ({
    subject: 'Verification Approved',
    html: `
      <h1>Verification Approved</h1>
      <p>Your ${type === 'id' ? 'ID' : 'address'} verification has been approved!</p>
      <p>Your account now has additional features and trust indicators.</p>
      <a href="https://subleasehero.com/profile" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        View Profile
      </a>
    `
  }),

  verificationRejected: (type: 'id' | 'address', reason: string) => ({
    subject: 'Verification Update Required',
    html: `
      <h1>Verification Update Needed</h1>
      <p>Your ${type === 'id' ? 'ID' : 'address'} verification could not be approved:</p>
      <p><strong>Reason:</strong> ${reason}</p>
      <p>Please submit updated documentation:</p>
      <a href="https://subleasehero.com/profile" style="display:inline-block;background:#4F46E5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;">
        Update Verification
      </a>
    `
  })
};

// Email service implementation
class EmailService {
  private static instance: EmailService;
  private constructor() {}

  static getInstance(): EmailService {
    if (!EmailService.instance) {
      EmailService.instance = new EmailService();
    }
    return EmailService.instance;
  }

  private async sendEmail(to: string, template: { subject: string; html: string }) {
    try {
      // In a real implementation, this would use an email service provider
      // like SendGrid, AWS SES, or similar
      console.log(`Sending email to ${to}:`, template);
      return true;
    } catch (error) {
      console.error('Failed to send email:', error);
      return false;
    }
  }

  async sendVerificationEmail(email: string, verificationLink: string) {
    return this.sendEmail(email, emailTemplates.verifyEmail(verificationLink));
  }

  async sendWelcomeEmail(user: User) {
    return this.sendEmail(user.email, emailTemplates.welcomeEmail(user));
  }

  async sendNewMessageNotification(recipient: User, sender: User, listingTitle: string) {
    return this.sendEmail(
      recipient.email,
      emailTemplates.newMessage(recipient.fullName || '', sender.fullName || '', listingTitle)
    );
  }

  async sendApplicationSubmittedNotification(owner: User, listing: Listing, request: LeaseRequest) {
    return this.sendEmail(owner.email, emailTemplates.applicationSubmitted(listing, request));
  }

  async sendApplicationApprovedNotification(applicant: User, listing: Listing) {
    return this.sendEmail(applicant.email, emailTemplates.applicationApproved(listing));
  }

  async sendApplicationRejectedNotification(applicant: User, listing: Listing) {
    return this.sendEmail(applicant.email, emailTemplates.applicationRejected(listing));
  }

  async sendDepositReceivedNotification(user: User, listing: Listing, amount: number) {
    return this.sendEmail(user.email, emailTemplates.depositReceived(listing, amount));
  }

  async sendVerificationApprovedNotification(user: User, type: 'id' | 'address') {
    return this.sendEmail(user.email, emailTemplates.verificationApproved(type));
  }

  async sendVerificationRejectedNotification(user: User, type: 'id' | 'address', reason: string) {
    return this.sendEmail(user.email, emailTemplates.verificationRejected(type, reason));
  }
}

export const emailService = EmailService.getInstance();